import Modal2 from 'components/commons/modal/Modal2';
import Loading from 'components/commons/loading/Loading';
import React, { useState } from 'react';
import * as consts from 'config/consts';
import { convertToMonoBrokerId, isMONOBrokerId } from '../utils/converter';
import { syncAccountAsset } from 'config/api';
import {
  SyncAccountAssetResponseProps,
  getSyncAccountAssetResultCodeMessage,
  getSyncAccountAssetResultCodeTitle,
} from './AccountUpdateStoppedModal';

type BrokerListProps = {
  id: string;
  name: string;
  url: string | null;
};

export const UpdateAlignmentAccountModal = ({
  brokerID: brokerID_,
  account,
  redirectPath,
  isShow,
  hide,
}: {
  brokerID: string;
  account: string;
  redirectPath: string;
  isShow: boolean;
  hide: () => void;
}) => {
  const [isShowSecondStep, setIsShowSecondStep] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [syncAccountAssetResponse, setSyncAccountAssetResponse] = useState<
    SyncAccountAssetResponseProps
  >({ tag: 'NotUpdated' });

  const brokerList = [
    {
      id: consts.MONOBrokerIds.SBI証券,
      name: 'SBI証券',
      url: 'https://www.sbisec.co.jp/ETGate/',
    },
    {
      id: consts.MONOBrokerIds.楽天証券,
      name: '楽天証券',
      url: 'https://www.rakuten-sec.co.jp/ITS/V_ACT_Login.html',
    },
    {
      id: consts.MONOBrokerIds.松井証券,
      name: '松井証券',
      url: 'https://www.matsui.co.jp/?mnu=hd',
    },
    {
      id: consts.MONOBrokerIds.三菱UFJeスマート証券,
      name: '三菱UFJ eスマート証券',
      url: 'https://kabu.com/',
    },
    {
      id: consts.MONOBrokerIds.マネックス証券,
      name: 'マネックス証券',
      url: 'https://www.monex.co.jp/',
    },
    {
      id: consts.MONOBrokerIds.野村證券,
      name: '野村證券',
      url: 'https://hometrade.nomura.co.jp/web/rmfIndexWebAction.do',
    },
    {
      id: consts.MONOBrokerIds.大和証券,
      name: '大和証券',
      url: 'https://www.daiwa.co.jp/PCC/HomeTrade/Account/m8301.html',
    },
    {
      id: consts.MONOBrokerIds.SMBC日興証券,
      name: 'SMBC日興証券',
      url: 'https://www.smbcnikko.co.jp/index.html',
    },
    {
      id: consts.MONOBrokerIds.岡三証券,
      name: '岡三証券',
      url: 'https://www.okasan.co.jp/',
    },
    {
      id: consts.MONOBrokerIds.みずほ証券,
      name: 'みずほ証券',
      url: 'https://www.mizuho-sc.com/index.html?rt_bn=sc_top_hd',
    },
    {
      id: consts.MONOBrokerIds.三菱UFJモルガンスタンレー証券,
      name: '三菱UFJモルガン・スタンレー証券',
      url: 'https://www.sc.mufg.jp/index.html',
    },
    {
      id: consts.MONOBrokerIds['JIS&T'],
      name: 'JIS&T',
      url: 'https://www.jis-t.ne.jp/dczws000/o3w',
    },
    {
      id: consts.MONOBrokerIds.NRK,
      name: 'NRK',
      url: null,
    },
    {
      id: consts.MONOBrokerIds.SBIベネフィットシステムズ,
      name: 'SBIベネフィットシステムズ',
      url: 'https://www.benefit401k.com/',
    },
  ] as BrokerListProps[];

  const brokerInfo = brokerList.find(list => {
    const brokerID = isMONOBrokerId(brokerID_)
      ? brokerID_
      : convertToMonoBrokerId(brokerID_);
    return list.id === brokerID;
  });

  const refreshAccountInfo = async (): Promise<void> => {
    setIsLoading(true);

    const ret = await syncAccountAsset(account);
    setSyncAccountAssetResponse({
      tag: 'Updated',
      resultCode: ret.ResultCode,
    });

    setIsLoading(false);
  };

  const close = () => {
    hide();
    window.location.href = redirectPath;
  };

  if (brokerInfo === undefined) return <></>;

  if (isLoading) {
    return (
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            <div className="font-bold text-bluegray-700 text-base">
              しばらくお待ちください
            </div>
            <div className="icon-close ml-auto text-gray-500 cursor-pointer" />
          </div>
          <div className="my-20 flex justify-center items-center">
            <Loading
              loading={true}
              circularLoading={true}
              style={{
                backgroundColor: 'transparent',
              }}
            />
          </div>
        </div>
      </Modal2>
    );
  }

  if (syncAccountAssetResponse.tag === 'Updated') {
    const resultCode = syncAccountAssetResponse.resultCode;

    return (
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            {resultCode !== 0 && (
              <div className="icon-alert items-start mr-2 mt-1 text-upcolor"></div>
            )}
            <div className="font-bold text-bluegray-700 text-base">
              {getSyncAccountAssetResultCodeTitle(resultCode)}
            </div>
            <div
              className="icon-close ml-auto text-gray-500 cursor-pointer"
              onClick={close}
            />
          </div>
          <div className="mt-3 text-sm">
            {getSyncAccountAssetResultCodeMessage(resultCode)}
          </div>
        </div>
      </Modal2>
    );
  }

  return (
    <>
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            <div className="icon-alert items-start mr-2 mt-1 text-upcolor"></div>
            <div className="font-bold text-bluegray-700 text-base">
              証券会社の多要素認証を解除し、 <br />
              連携口座を更新してください
            </div>
            <div
              className="icon-close ml-auto text-gray-500 cursor-pointer"
              onClick={hide}
            />
          </div>
          <div className="text-sm mt-3">
            証券会社のログインに多要素認証を設定している方は下記の手順で連携口座を更新してください。
          </div>
          <div className="text-right text-sm text-link">
            <a
              href="https://support.minkabu.jp/hc/ja/articles/17607041083289-%E5%A4%9A%E8%A6%81%E7%B4%A0%E8%AA%8D%E8%A8%BC%E3%81%A8%E3%81%AF"
              target="_blank"
              rel="noopener noreferrer">
              多要素認証とは
            </a>
          </div>
          <div className="mt-4 text-sm font-bold">連携口座更新のステップ</div>
          <div className="flex flex-row mt-1 text-sm">
            <span>1.</span>
            <span className="ml-1">
              証券会社の公式サイトで多要素認証を解除してください。
            </span>
          </div>
          {brokerInfo.url && (
            <a href={brokerInfo.url} target="_blank" rel="noopener noreferrer">
              <div className="border rounded-lg border-blue-700 bg-gray-50 flex items-center justify-center py-3 mt-4 shadow">
                <div className="text-sm text-blue-700">
                  {brokerInfo.name}の公式サイトへ
                </div>
              </div>
            </a>
          )}
          <div className="mt-4 border-b border-gray-300" />
          {!isShowSecondStep ? (
            <div
              className="flex flex-col items-center mt-6 text-blue-700 cursor-pointer"
              onClick={(): void => setIsShowSecondStep(prev => !prev)}>
              <div className="text-xs">証券会社でお手続きを済ませた方は</div>
              <div className="mt-1 text-base font-bold">次のステップへ</div>
              <div className="icon-chevron-down mt-3 transform rotate-0 text-2xl text-bluegray-500 transition duration-300"></div>
            </div>
          ) : (
            <>
              <div className="flex flex-row mt-4 text-sm">
                <span>2.</span>
                <span className="ml-1">
                  下記のボタンを押して口座情報を更新してください。
                </span>
              </div>
              <button
                className="mt-4 py-3 text-white bg-bluegray-700 justify-center w-full text-base rounded-lg"
                onClick={refreshAccountInfo}>
                口座情報を更新
              </button>
            </>
          )}
        </div>
      </Modal2>
    </>
  );
};
